// Track init per live DOM node (not via a data-* attribute, which would survive
// the AJAX innerHTML swap and wrongly mark fresh, listener-less nodes as initialized).
const initializedResources = new WeakSet();
const initializedGuides = new WeakSet();

export function initWsapResources() {
  const container = document.getElementById("wsap-resources");
  if (!container) return;
  if (initializedResources.has(container)) return;

  // ── DOM refs ────────────────────────────────────────────────────────────────
  const searchInput   = document.getElementById("wsap-resource-search");
  const listEl        = document.getElementById("wsap-resource-list");
  const loaderEl      = document.getElementById("wsap-resource-loader");
  const emptyEl       = document.getElementById("wsap-resource-empty");
  const statusEl      = document.getElementById("wsap-resource-status");
  const countEl       = document.getElementById("wsap-resource-count");
  const clearBtn      = document.getElementById("wsap-resource-clear");
  const pillsEl       = document.getElementById("wsap-resource-pills");
  const activeBarEl   = document.getElementById("wsap-resource-active-bar");
  const loadMoreBtn   = document.getElementById("wsap-resource-load-more");
  const typeCheckboxes     = container.querySelectorAll(".wsap-resource-type-filter");
  const divisionCheckboxes = container.querySelectorAll(".wsap-resource-division-filter");

  // Bail if critical rendering elements are missing — avoids cascading TypeErrors
  if (!listEl || !loaderEl || !emptyEl) return;
  initializedResources.add(container);

  // ── Config ──────────────────────────────────────────────────────────────────
  const themeUri = container.dataset.themeUri || "";
  const PER_PAGE = 5;

  // ── State ───────────────────────────────────────────────────────────────────
  let currentPage     = 1;
  let isAppending     = false;
  let debounceTimer   = null;
  let abortController = null;

  // ── Helpers ─────────────────────────────────────────────────────────────────
  function escHtml(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  // Decode HTML entities (e.g. &amp; &#8217;) coming from the data source
  function decodeHtml(str) {
    const txt = document.createElement("textarea");
    txt.innerHTML = String(str ?? "");
    return txt.value;
  }

  // Safe CSS selector attribute value — prevents broken selectors from unexpected slugs
  function cssAttr(val) {
    return typeof CSS !== "undefined" && CSS.escape
      ? CSS.escape(val)
      : val.replace(/["\\]/g, "\\$&");
  }

  // ── UI state helpers ────────────────────────────────────────────────────────
  function showLoading(state) {
    loaderEl.classList.toggle("hidden", !state);
    if (state) {
      emptyEl.classList.add("hidden");
      listEl.classList.add("hidden");
    }
  }

  function showEmpty(state) {
    emptyEl.classList.toggle("hidden", !state);
  }

  function setLoadMoreVisible(visible) {
    loadMoreBtn?.classList.toggle("hidden", !visible);
  }

  // ── Filters ─────────────────────────────────────────────────────────────────
  function getActiveFilters() {
    const types = [];
    const divisions = [];
    typeCheckboxes.forEach((cb) => { if (cb.checked) types.push(cb.value); });
    divisionCheckboxes.forEach((cb) => { if (cb.checked) divisions.push(cb.value); });
    return {
      search: searchInput?.value.trim() ?? "",
      types,
      divisions,
    };
  }

  // ── Pills ───────────────────────────────────────────────────────────────────
  function createPill(label, slug, group) {
    const pill = document.createElement("span");
    pill.className =
      "inline-flex items-center gap-2 bg-brand-dark-red border border-brand-dark-red !pl-6 p-2 rounded-full text-p1 text-white cursor-pointer";
    pill.innerHTML = `
      ${escHtml(label)}
      <button class="flex-shrink-0 cursor-pointer" aria-label="Remove ${escHtml(label)} filter">
        <img src="${themeUri}/resources/images/red-close-btn-with-white-bg.svg"
          class="w-[1.875rem] h-[1.875rem] object-contain pointer-events-none" alt="">
      </button>`;
    pill.addEventListener("click", () => {
      const selector =
        group === "type" ? ".wsap-resource-type-filter" : ".wsap-resource-division-filter";
      const cb = container.querySelector(`${selector}[value="${cssAttr(slug)}"]`);
      if (cb) cb.checked = false;
      fetchResources(false);
    });
    return pill;
  }

  function renderPills(filters) {
    if (!pillsEl) return;
    pillsEl.innerHTML = "";
    filters.types.forEach((slug) => {
      const cb = container.querySelector(`.wsap-resource-type-filter[value="${cssAttr(slug)}"]`);
      const label = cb?.closest("label")?.querySelector("span")?.textContent?.trim() ?? slug;
      pillsEl.appendChild(createPill(label, slug, "type"));
    });
    filters.divisions.forEach((slug) => {
      const cb = container.querySelector(`.wsap-resource-division-filter[value="${cssAttr(slug)}"]`);
      const label = cb?.closest("label")?.querySelector("span")?.textContent?.trim() ?? slug;
      pillsEl.appendChild(createPill(label, slug, "division"));
    });
  }

  function updateStatus(total, filters) {
    const hasFilters = filters.search || filters.types.length || filters.divisions.length;
    activeBarEl?.classList.toggle("hidden", !hasFilters);
    if (hasFilters) {
      statusEl?.classList.remove("hidden");
      if (countEl) countEl.textContent = `${total} Result${total !== 1 ? "s" : ""}`;
    } else {
      statusEl?.classList.add("hidden");
    }
    renderPills(filters);
  }

  // ── Card renderer ────────────────────────────────────────────────────────────
  function renderCard(doc) {
    if (!doc || typeof doc !== "object") return "";

    const title       = doc.title ?? "";
    const date        = doc.resource_date ?? "";
    const version     = doc.version ?? "";
    const description = doc.description ?? "";
    const fileUrl     = doc.resource_file_url ?? "";
    const hasLink     = !!fileUrl;

    // Login gate: send logged-out visitors to the login page when enabled for this resource.
    const isLoggedIn  = typeof envConfig !== "undefined" && envConfig.IS_USER_LOGGED_IN;
    const isGated     = !!doc.enable_login_gate && !isLoggedIn;
    const loginText   = doc.login_button_text || "Login to View";
    const linkUrl     = isGated
      ? `${envConfig.LOGIN_PAGE_URL}?redirect_to=${encodeURIComponent(window.location.href)}`
      : fileUrl;
    const linkTarget  = isGated ? "" : `target="_blank" rel="noopener noreferrer"`;

    const cardInner = `
      <div class="flex-1 flex flex-col items-center sm:items-start gap-2">
        <h4 class="w-full text-[1.5625rem] leading-[2.0625rem] text-brand-dark-blue font-semibold !my-0 text-center sm:text-start">
          ${escHtml(decodeHtml(title))}
        </h4>
        ${description ? `
        <div class="w-full htmlBlock htmlBlock-para-style-after-sm text-brand-black !my-0">
          ${description}
        </div>` : ""}
        ${date || version ? `
        <div class="flex items-center gap-3 flex-wrap">
          ${date    ? `<span class="text-p1 text-brand-dark-red font-normal">${escHtml(date)}</span>` : ""}
          ${date && version ? `<span class="text-p1 text-brand-dark-red font-normal">•</span>` : ""}
          ${version ? `<span class="text-p1 text-brand-dark-red font-normal">Version ${escHtml(version)}</span>` : ""}
        </div>` : ""}
      </div>
      ${hasLink ? (isGated ? `
      <div class="flex-shrink-0 self-start sm:self-center mt-2 sm:mt-0 max-sm:mx-auto">
        <span class="inline-block py-3 px-5 bg-brand-warm-beige text-white rounded-[.3125rem] text-[1.25rem] leading-[1.5625rem] whitespace-nowrap">
          ${escHtml(loginText)}
        </span>
      </div>` : `
      <div class="flex-shrink-0 self-start sm:self-center mt-2 sm:mt-0 max-sm:mx-auto">
        <img src="${themeUri}/resources/images/download-icon-warm-beige.svg"
          alt="Download PDF" class="w-[2.5rem] h-[2.5rem] object-contain">
      </div>`) : ""}`;

    if (hasLink) {
      return `
      <a href="${escHtml(linkUrl)}" ${linkTarget}
        class="w-full flex flex-col sm:flex-row items-center gap-4 lg:gap-10 xl:gap-12 py-4 border-b border-brand-warm-gray bg-white transition-transform duration-300 ease-in-out hover:scale-[1.01] cursor-pointer no-underline hover:no-underline">
        ${cardInner}
      </a>`;
    }

    return `
    <div class="w-full flex flex-col sm:flex-row items-center gap-4 lg:gap-10 xl:gap-12 py-4 border-b border-brand-warm-gray bg-white">
      ${cardInner}
    </div>`;
  }

  // ── Fetch ────────────────────────────────────────────────────────────────────
  async function fetchResources(append = false) {
    // Typesense config guard
    if (
      typeof envConfig === "undefined" ||
      !envConfig.TYPESENSE_BASE_URL?.startsWith("http")
    ) {
      if (!append) {
        showLoading(false);
        showEmpty(true);
        listEl.classList.add("hidden");
        setLoadMoreVisible(false);
        updateStatus(0, getActiveFilters());
      }
      return;
    }

    // Prevent Load More double-fire
    if (append && isAppending) return;

    if (!append) {
      // Cancel any in-flight request (including a pending append)
      abortController?.abort();
      isAppending = false;
      currentPage = 1;
      showLoading(true);
      listEl.innerHTML = "";
    }

    if (append) {
      isAppending = true;
      if (loadMoreBtn) {
        loadMoreBtn.disabled = true;
        loadMoreBtn.textContent = "Loading...";
      }
    }
    abortController = new AbortController();
    setLoadMoreVisible(!append); // hide for filter/search; keep visible (disabled) for append

    const filters = getActiveFilters();

    const params = new URLSearchParams({
      q:          filters.search || "*",
      query_by:   "title,searchable_text",
      sort_by:    "publish_date:desc",
      per_page:   PER_PAGE,
      page:       currentPage,
      num_typos:  0,
      prefix:     false,
    });

    const filterParts = [];
    if (filters.types.length) {
      filterParts.push(
        `wsap_resource_type:=[${filters.types.map((t) => `\`${t}\``).join(",")}]`,
      );
    }
    if (filters.divisions.length) {
      filterParts.push(
        `wsap_division:=[${filters.divisions.map((d) => `\`${d}\``).join(",")}]`,
      );
    }
    if (filterParts.length) params.set("filter_by", filterParts.join(" && "));

    try {
      const url =
        `${envConfig.TYPESENSE_BASE_URL}:${envConfig.TYPESENSE_PORT}` +
        `/collections/wsap_resource/documents/search?${params}`;

      const response = await fetch(url, {
        headers: { "X-TYPESENSE-API-KEY": envConfig.TYPESENSE_SEARCH_ONLY_KEY },
        signal: abortController.signal,
        keepalive: true,
      });

      // Treat non-2xx as an error so bad responses don't silently render nothing
      if (!response.ok) {
        throw new Error(`Typesense error: ${response.status}`);
      }

      const data = await response.json();
      const hits  = Array.isArray(data?.hits) ? data.hits : [];
      const total = typeof data?.found === "number" ? data.found : hits.length;

      if (hits.length > 0) {
        const html = hits
          .map((hit) => renderCard(hit?.document))
          .filter(Boolean)
          .join("");

        if (append) {
          listEl.insertAdjacentHTML("beforeend", html);
        } else {
          showLoading(false);
          showEmpty(false);
          listEl.classList.remove("hidden");
          listEl.innerHTML = html;
        }

        updateStatus(total, filters);
        setLoadMoreVisible(total > currentPage * PER_PAGE);
      } else if (!append) {
        showLoading(false);
        showEmpty(true);
        listEl.classList.add("hidden");
        updateStatus(0, filters);
      }
    } catch (err) {
      // AbortError is expected when a new filter supersedes this request — not an error
      if (err.name === "AbortError") return;

      console.error("WSAP resources fetch failed:", err);

      if (append) {
        // Restore Load More button so the user can retry
        setLoadMoreVisible(true);
      } else {
        showLoading(false);
        showEmpty(true);
        listEl.classList.add("hidden");
      }
    } finally {
      if (append) {
        isAppending = false;
        if (loadMoreBtn) {
          loadMoreBtn.disabled = false;
          loadMoreBtn.textContent = "Load More";
        }
      }
    }
  }

  // ── Event listeners ──────────────────────────────────────────────────────────
  searchInput?.addEventListener("input", () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => fetchResources(false), 300);
  });

  typeCheckboxes.forEach((cb) =>
    cb.addEventListener("change", () => fetchResources(false)),
  );
  divisionCheckboxes.forEach((cb) =>
    cb.addEventListener("change", () => fetchResources(false)),
  );

  clearBtn?.addEventListener("click", () => {
    if (searchInput) searchInput.value = "";
    typeCheckboxes.forEach((cb) => { cb.checked = false; });
    divisionCheckboxes.forEach((cb) => { cb.checked = false; });
    fetchResources(false);
  });

  loadMoreBtn?.addEventListener("click", () => {
    currentPage++;
    fetchResources(true);
  });

  // ── Initial load ─────────────────────────────────────────────────────────────
  fetchResources(false);
}

// ── Guidelines accordions ────────────────────────────────────────────────────
export function initWsapGuidelines() {
  document.querySelectorAll(".wsap-guide-trigger").forEach((trigger) => {
    if (initializedGuides.has(trigger)) return;
    initializedGuides.add(trigger);

    trigger.addEventListener("click", (e) => {
      e.stopImmediatePropagation();
      const targetId = trigger.dataset.target;
      const content = document.getElementById(targetId);
      if (!content) return;
      const isOpen = content.classList.contains("grid-rows-[1fr]");
      if (isOpen) {
        content.classList.remove("grid-rows-[1fr]", "opacity-100");
        content.classList.add("grid-rows-[0fr]", "opacity-0");
        trigger.setAttribute("aria-expanded", "false");
      } else {
        content.classList.remove("grid-rows-[0fr]", "opacity-0");
        content.classList.add("grid-rows-[1fr]", "opacity-100");
        trigger.setAttribute("aria-expanded", "true");
      }
    });

    // Keyboard support — Enter / Space toggle
    trigger.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        trigger.click();
      }
    });
  });
}

function initWsapResourcesAll() {
  initWsapResources();
  initWsapGuidelines();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initWsapResourcesAll);
} else {
  initWsapResourcesAll();
}

document.addEventListener("ajax:loaded", initWsapResourcesAll);
